import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { httpsCallable, getFunctions } from "firebase/functions";
import { doc, getDoc, collection, query, where, getDocs, deleteDoc } from "firebase/firestore";
import { Icons } from "../../lib/Icons";

// Template type metadata for display
const TEMPLATE_TYPES = [
  {
    type: 'social_story_comic',
    name: 'Social Story',
    description: 'Comic strip style story with symbols and speech bubbles',
    icon: 'ChatBubble',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    type: 'visual_schedule',
    name: 'Visual Schedule',
    description: 'Sequential steps with symbols and checkboxes',
    icon: 'ClipboardList',
    color: 'from-green-500 to-emerald-500',
  },
  {
    type: 'choice_board',
    name: 'Choice Board',
    description: 'Grid of options for making choices',
    icon: 'Puzzle',
    color: 'from-purple-500 to-violet-500',
  },
  {
    type: 'reward_chart',
    name: 'Reward Chart',
    description: 'Token board for tracking behaviors',
    icon: 'Star',
    color: 'from-amber-500 to-orange-500',
  },
  {
    type: 'emotion_cards',
    name: 'Emotion Cards',
    description: 'Cards for identifying and discussing feelings',
    icon: 'Heart',
    color: 'from-pink-500 to-rose-500',
  },
  {
    type: 'task_analysis',
    name: 'Task Analysis',
    description: 'Step-by-step breakdown with data tracking',
    icon: 'Document',
    color: 'from-indigo-500 to-blue-500',
  },
  {
    type: 'now_next_board',
    name: 'Now and Next Board',
    description: 'Simple motivational board',
    icon: 'ArrowRight',
    color: 'from-teal-500 to-cyan-500',
  },
  {
    type: 'worksheet',
    name: 'Worksheet',
    description: 'Secondary intervention worksheets with optional symbols',
    icon: 'Pencil',
    color: 'from-slate-500 to-zinc-600',
  },
];

// Pre-made template examples
const PREMADE_TEMPLATES = {
  visual_schedule: [
    { id: 'morning-routine', name: 'Morning Routine', steps: 10 },
    { id: 'school-day', name: 'School Day', steps: 10 },
    { id: 'bedtime-routine', name: 'Bedtime Routine', steps: 7 },
    { id: 'lesson-structure', name: 'Lesson Structure', steps: 6 },
    { id: 'pe-lesson', name: 'PE Lesson', steps: 7 },
  ],
  social_story_comic: [
    { id: 'going-to-dentist', name: 'Going to the Dentist', panels: 6 },
    { id: 'fire-drill', name: 'Fire Drill', panels: 6 },
    { id: 'making-friends', name: 'Making Friends', panels: 6 },
    { id: 'losing-a-game', name: 'Losing a Game', panels: 6 },
    { id: 'waiting-my-turn', name: 'Waiting My Turn', panels: 6 },
  ],
  choice_board: [
    { id: 'break-time-choices', name: 'Break Time Choices', choices: 6 },
    { id: 'snack-choices', name: 'Snack Choices', choices: 6 },
    { id: 'drink-choices', name: 'Drink Choices', choices: 4 },
    { id: 'emotion-check-in', name: 'How Are You Feeling?', choices: 6 },
    { id: 'reward-choices', name: 'Reward Choices', choices: 6 },
  ],
  task_analysis: [
    { id: 'hand-washing', name: 'Hand Washing', steps: 8 },
    { id: 'getting-dressed', name: 'Getting Dressed', steps: 6 },
    { id: 'using-toilet', name: 'Using the Toilet', steps: 8 },
    { id: 'brush-teeth', name: 'Brushing Teeth', steps: 9 },
  ],
  now_next_board: [
    { id: 'now-work-next-play', name: 'Now Work, Next Play' },
    { id: 'now-tidy-next-snack', name: 'Now Tidy, Next Snack' },
    { id: 'now-listen-next-choose', name: 'Now Listen, Next Choose' },
  ],
  reward_chart: [
    { id: 'good-sitting', name: 'Good Sitting Chart', stars: 5 },
    { id: 'kind-hands', name: 'Kind Hands Chart', stars: 5 },
    { id: 'listening-ears', name: 'Listening Chart', stars: 5 },
  ],
  worksheet: [
    { id: 'reflection-session', name: 'Session Reflection', type: 'reflection' },
    { id: 'reflection-with-symbols', name: 'Session Reflection (with symbols)', type: 'reflection' },
    { id: 'goal-setting-smart', name: 'SMART Goal Setting', type: 'goal_setting' },
    { id: 'goal-setting-with-symbols', name: 'SMART Goal Setting (with symbols)', type: 'goal_setting' },
    { id: 'emotion-checkin-basic', name: 'Emotion Check-In', type: 'emotion_checkin' },
    { id: 'emotion-checkin-with-symbols', name: 'Emotion Check-In (with symbols)', type: 'emotion_checkin' },
  ],
  emotion_cards: [
    { id: 'basic-emotions', name: 'Basic Emotions', cards: 6 },
    { id: 'zones-of-regulation', name: 'Zones of Regulation', cards: 12 },
  ],
};

// Domain styling for KB activity tiles (matches SoloDashboard)
const KB_DOMAINS = [
  { id: 'Communication', label: 'Communication', Icon: Icons.ChatBubble, color: 'from-blue-400/80 to-cyan-400/40', iconColor: 'text-blue-400' },
  { id: 'SEMH', label: 'Social & Emotional', Icon: Icons.Heart, color: 'from-green-400/80 to-emerald-400/40', iconColor: 'text-green-400' },
  { id: 'Cognition', label: 'Cognition & Learning', Icon: Icons.Brain, color: 'from-purple-400/80 to-violet-400/40', iconColor: 'text-purple-400' },
  { id: 'Sensory/Physical', label: 'Sensory & Physical', Icon: Icons.Hand, color: 'from-orange-400/80 to-amber-400/40', iconColor: 'text-orange-400' },
];
const getKBDomainStyle = (domainId) => {
  const domain = KB_DOMAINS.find(d => d.id === domainId);
  if (!domain) return { gradient: 'from-slate-600/60 to-slate-700/30', iconColor: 'text-slate-400', Icon: Icons.Puzzle };
  return { gradient: domain.color, iconColor: domain.iconColor, Icon: domain.Icon };
};

const ResourceBuilder = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedPremade, setSelectedPremade] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [customMode, setCustomMode] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Custom template state
  const [customTitle, setCustomTitle] = useState('');
  const [customItems, setCustomItems] = useState([{ word: '', label: '' }]);
  const [studentName, setStudentName] = useState('');
  const [showSymbols, setShowSymbols] = useState(false); // For worksheets
  const [worksheetType, setWorksheetType] = useState('reflection'); // For worksheets

  // Editing template state
  const [editingTemplate, setEditingTemplate] = useState(null); // Tracks the template being edited
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // User templates state
  const [userTemplates, setUserTemplates] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateNameToSave, setTemplateNameToSave] = useState('');
  const [lastGeneratedTemplate, setLastGeneratedTemplate] = useState(null);

  // KB activities for "All resources" section (non-editable browse, same as homepage)
  const [kbActivities, setKbActivities] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        // Allow browsing in preview mode instead of redirecting
        setIsPreviewMode(true);
        setLoading(false);
        return;
      }
      setUser(currentUser);
      setIsPreviewMode(false);
      setLoading(false);
      
      // Load user templates if authenticated
      if (currentUser) {
        loadUserTemplates();
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load KB activities for "All resources" section (same as SoloDashboard homepage)
  useEffect(() => {
    const loadKbActivities = async () => {
      try {
        const kbRef = collection(db, 'kb_items');
        const snapshot = await getDocs(kbRef);
        const list = [];
        snapshot.forEach(docSnap => {
          list.push({ id: docSnap.id, ...docSnap.data() });
        });
        setKbActivities(list);
      } catch (err) {
        console.error('Error loading KB activities:', err);
      }
    };
    loadKbActivities();
  }, []);

  // Load user templates
  const loadUserTemplates = async () => {
    if (!user) return;
    
    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const getTemplatesFunc = httpsCallable(functions, 'getPremadeTemplatesFunc');
      const result = await getTemplatesFunc({
        includeUserTemplates: true,
      });
      
      const templates = result.data.templates || [];
      const userTemplatesList = templates.filter(t => t.isUserTemplate === true);
      setUserTemplates(userTemplatesList);
    } catch (error) {
      console.error('Error loading user templates:', error);
    }
  };

  // Save custom template
  const saveCustomTemplate = async (templateName) => {
    if (!templateName || !templateName.trim()) {
      setError('Please enter a template name');
      return;
    }

    const template = buildCustomTemplate();
    if (!template) {
      setError('Cannot save: template is invalid');
      return;
    }

    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const saveFunc = httpsCallable(functions, 'saveUserTemplateFunc');
      const result = await saveFunc({
        template,
        title: templateName.trim(),
      });

      if (result.data.success) {
        setShowSaveDialog(false);
        setTemplateNameToSave('');
        setError(null);
        // Reload user templates
        await loadUserTemplates();
        // Show success message (you could add a toast here)
        alert('Template saved successfully!');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setError(error.message || 'Failed to save template');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setSelectedPremade(null);
    setCustomMode(false);
    setError(null);
  };

  const handlePremadeSelect = (premade) => {
    setSelectedPremade(premade);
    setCustomMode(false);
  };

  const handleCustomMode = () => {
    setCustomMode(true);
    setSelectedPremade(null);
    setEditingTemplate(null);
    setCustomItems([{ word: '', label: '' }]);
    setCustomTitle('');
  };

  // Load a premade template for editing in custom mode
  const loadTemplateForEditing = async (premade) => {
    setLoadingTemplate(true);
    setError(null);

    try {
      // Determine which collection to fetch from
      const collectionName = premade.isUserTemplate ? 'user_templates' : 'premade_templates';
      const docRef = doc(db, collectionName, premade.id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        setError('Template not found');
        setLoadingTemplate(false);
        return;
      }

      const templateData = docSnap.data();

      // Map template data to custom form based on type
      let items = [];
      let title = templateData.title || premade.name;

      switch (templateData.type) {
        case 'visual_schedule':
          items = (templateData.steps || []).map(step => ({
            word: step.symbolWord || '',
            label: step.label || '',
          }));
          break;

        case 'choice_board':
          items = (templateData.choices || []).map(choice => ({
            word: choice.symbolWord || '',
            label: choice.label || '',
          }));
          break;

        case 'social_story_comic':
          items = (templateData.panels || []).map(panel => ({
            word: panel.symbolWord || '',
            label: panel.caption || '',
          }));
          break;

        case 'now_next_board':
          items = [
            { word: templateData.nowSymbol || '', label: templateData.nowLabel || '' },
            { word: templateData.nextSymbol || '', label: templateData.nextLabel || '' },
          ];
          break;

        case 'emotion_cards':
          items = (templateData.emotions || []).map(emotion => ({
            word: emotion.symbolWord || '',
            label: emotion.label || '',
          }));
          break;

        case 'task_analysis':
          items = (templateData.steps || []).map(step => ({
            word: step.symbolWord || '',
            label: step.instruction || '',
          }));
          break;

        case 'reward_chart':
          items = (templateData.targets || []).map(target => ({
            word: target.symbolWord || '',
            label: target.label || '',
          }));
          break;

        case 'worksheet':
          // Handle different worksheet types
          setShowSymbols(templateData.showSymbols || false);
          setWorksheetType(templateData.worksheetType || 'reflection');
          if (templateData.worksheetType === 'reflection' && templateData.prompts) {
            items = templateData.prompts.map(p => ({
              word: p.symbolWord || '',
              label: p.question || '',
              lines: p.lines || 3,
            }));
          } else if (templateData.worksheetType === 'emotion_checkin' && templateData.emotions) {
            items = templateData.emotions.map(e => ({
              word: e.symbolWord || '',
              label: e.emotion || '',
              color: e.color || '',
            }));
          } else {
            items = [{ word: '', label: '', lines: 3 }];
          }
          break;

        default:
          items = [{ word: '', label: '' }];
      }

      // Update state
      setCustomTitle(title);
      setCustomItems(items.length > 0 ? items : [{ word: '', label: '' }]);
      setEditingTemplate(premade);
      setCustomMode(true);
      setSelectedPremade(null);

    } catch (err) {
      console.error('Error loading template:', err);
      setError('Failed to load template for editing');
    } finally {
      setLoadingTemplate(false);
    }
  };

  const addCustomItem = () => {
    setCustomItems([...customItems, { word: '', label: '' }]);
  };

  const removeCustomItem = (index) => {
    setCustomItems(customItems.filter((_, i) => i !== index));
  };

  const updateCustomItem = (index, field, value) => {
    const updated = [...customItems];
    updated[index][field] = value;
    setCustomItems(updated);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);

    try {
      const functions = getFunctions(undefined, 'europe-west3');

      if (selectedPremade) {
        // Generate from premade or user template
        const generateFunc = httpsCallable(functions, 'generateFromPremadeFunc');
        const result = await generateFunc({
          templateId: selectedPremade.id,
          customizations: {
            studentName: studentName || undefined,
          },
        });

        if (result.data.downloadUrl) {
          // Open download URL
          window.open(result.data.downloadUrl, '_blank');
        } else if (result.data.pdfBase64) {
          // Download from base64
          downloadPdfFromBase64(result.data.pdfBase64, result.data.filename);
        }
      } else if (customMode) {
        // Generate custom template
        const template = buildCustomTemplate();
        if (!template) {
          setError('Please fill in all required fields');
          setGenerating(false);
          return;
        }

        const generateFunc = httpsCallable(functions, 'generateResourceFunc');
        const result = await generateFunc({
          templateType: selectedType,
          template,
          options: {
            studentName: studentName || undefined,
          },
          saveToStorage: true,
        });

        if (result.data.downloadUrl) {
          window.open(result.data.downloadUrl, '_blank');
        } else if (result.data.pdfBase64) {
          downloadPdfFromBase64(result.data.pdfBase64, result.data.filename);
        }

        // After successful generation, offer to save template
        if (customMode && template) {
          setLastGeneratedTemplate(template);
          setShowSaveDialog(true);
        }
      }
    } catch (err) {
      console.error('Error generating resource:', err);
      setError(err.message || 'Failed to generate resource');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdfFromBase64 = (base64Data, filename) => {
    const link = document.createElement('a');
    link.href = base64Data;
    link.download = filename || 'resource.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const buildCustomTemplate = () => {
    const validItems = customItems.filter(item => item.word.trim());
    if (validItems.length === 0) return null;

    switch (selectedType) {
      case 'visual_schedule':
        return {
          type: 'visual_schedule',
          title: customTitle || 'My Schedule',
          steps: validItems.map(item => ({
            symbolWord: item.word,
            label: item.label || item.word,
          })),
          includeCheckboxes: true,
          includeTime: false,
          layout: 'vertical',
          scheduleType: 'activity',
        };

      case 'choice_board':
        return {
          type: 'choice_board',
          title: customTitle || 'My Choices',
          choices: validItems.map(item => ({
            symbolWord: item.word,
            label: item.label || item.word,
          })),
          layout: validItems.length <= 4 ? '2x2' : validItems.length <= 6 ? '2x3' : '3x3',
        };

      case 'now_next_board':
        if (validItems.length < 2) return null;
        return {
          type: 'now_next_board',
          title: customTitle || 'Now and Next Board',
          nowSymbol: validItems[0].word,
          nowLabel: validItems[0].label || validItems[0].word,
          nextSymbol: validItems[1].word,
          nextLabel: validItems[1].label || validItems[1].word,
          studentName,
        };

      case 'emotion_cards':
        return {
          type: 'emotion_cards',
          title: customTitle || 'My Emotions',
          emotions: validItems.map(item => ({
            symbolWord: item.word,
            label: item.label || item.word,
          })),
          includeStrategies: false,
          useZonesColors: false,
          cardsPerRow: 3,
        };

      case 'social_story_comic':
        return {
          type: 'social_story_comic',
          title: customTitle || 'My Story',
          panels: validItems.map(item => ({
            symbolWord: item.word,
            caption: item.label || item.word,
          })),
          panelsPerRow: 3,
          showSpeechBubbles: false,
          showThoughtBubbles: false,
          showFeelings: false,
        };

      case 'task_analysis':
        return {
          type: 'task_analysis',
          title: customTitle || 'Task Analysis',
          taskName: customTitle || 'My Task',
          steps: validItems.map((item, index) => ({
            stepNumber: index + 1,
            symbolWord: item.word,
            instruction: item.label || item.word,
          })),
          includePromptColumn: true,
          includeDataColumn: true,
        };

      case 'reward_chart':
        return {
          type: 'reward_chart',
          title: customTitle || 'My Reward Chart',
          targets: validItems.slice(0, 3).map(item => ({
            symbolWord: item.word,
            label: item.label || item.word,
          })),
          starsToEarn: 5,
          rewardSymbol: 'star',
          rewardLabel: 'Well done!',
          periodType: 'daily',
        };

      case 'worksheet':
        if (worksheetType === 'reflection') {
          return {
            type: 'worksheet',
            title: customTitle || 'Session Reflection',
            worksheetType: 'reflection',
            showSymbols,
            studentName,
            prompts: validItems.map(item => ({
              question: item.label || 'What did you notice?',
              lines: item.lines || 3,
              symbolWord: item.word || undefined,
            })),
          };
        } else if (worksheetType === 'goal_setting') {
          return {
            type: 'worksheet',
            title: customTitle || 'SMART Goal Setting',
            worksheetType: 'goal_setting',
            showSymbols,
            studentName,
            goalAreas: validItems.map(item => ({
              area: item.label || item.word,
              symbolWord: item.word || undefined,
            })),
          };
        } else if (worksheetType === 'emotion_checkin') {
          return {
            type: 'worksheet',
            title: customTitle || 'Emotion Check-In',
            worksheetType: 'emotion_checkin',
            showSymbols,
            studentName,
            checkInType: 'both',
            scaleType: '1-5',
            emotions: validItems.map(item => ({
              emotion: item.label || item.word,
              symbolWord: item.word || undefined,
              color: item.color || undefined,
            })),
          };
        }
        return null;

      default:
        return null;
    }
  };

  const handleBack = () => {
    if (customMode || selectedPremade) {
      setCustomMode(false);
      setSelectedPremade(null);
      setEditingTemplate(null);
    } else if (selectedType) {
      setSelectedType(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/solo/dashboard" className="flex items-center gap-3">
              <img
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Ed"
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-white">SEN-Ed</span>
            </Link>

            <div className="flex items-center gap-4">
              <Link
                to="/solo/symbols"
                className="text-slate-400 hover:text-white transition-colors text-sm"
              >
                Symbol Generator
              </Link>
              <Link
                to="/solo/dashboard"
                className="text-slate-400 hover:text-white transition-colors"
              >
                ← Dashboard
              </Link>

              <div className="relative group">
                <button className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors">
                  {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-slate-800 rounded-xl shadow-xl border border-slate-700 py-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all">
                  <div className="px-4 py-2 border-b border-slate-700">
                    <p className="text-sm text-white font-medium truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 via-slate-900 to-purple-900/30"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-300 rounded-full text-sm mb-4">
              <Icons.Document className="w-4 h-4" />
              Resource Builder
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Create
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text"> Printable Resources</span>
            </h1>
            <p className="text-lg text-slate-300">
              Build custom visual schedules, social stories, choice boards, and more with ARASAAC symbols.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        {(selectedType || customMode || selectedPremade) && (
          <button
            onClick={handleBack}
            className="mb-6 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <Icons.ArrowRight className="w-4 h-4 rotate-180" />
            Back
          </button>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
            <p className="flex items-center gap-2">
              <Icons.ExclamationTriangle className="w-5 h-5" />
              {error}
            </p>
          </div>
        )}

        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <div className="mb-8 p-6 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-2xl border border-indigo-500/30">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-indigo-500/30 flex items-center justify-center">
                <Icons.Document className="w-6 h-6 text-indigo-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-1">Preview Mode</h3>
                <p className="text-slate-300 text-sm">
                  Explore all resource templates. Sign up to generate custom visual schedules, social stories,
                  choice boards, and more with ARASAAC symbols.
                </p>
              </div>
              <button
                onClick={() => navigate('/signup/solo')}
                className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all flex items-center gap-2"
              >
                <Icons.LockClosed className="w-4 h-4" />
                Sign Up Free
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Select Template Type */}
        {!selectedType && (
          <div>
            <h2 className="text-xl font-semibold text-white mb-6">
              Choose an editable resource
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {TEMPLATE_TYPES.map((type) => {
                const IconComponent = Icons[type.icon] || Icons.Document;
                return (
                  <button
                    key={type.type}
                    onClick={() => handleTypeSelect(type.type)}
                    className={`p-6 rounded-2xl text-left transition-all hover:scale-105 bg-gradient-to-br ${type.color} hover:shadow-lg hover:shadow-${type.color.split('-')[1]}-500/20`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-white mb-1">{type.name}</h3>
                    <p className="text-sm text-white/80">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Select Premade or Custom */}
        {selectedType && !customMode && !selectedPremade && (
          <div>
            <h2 className="text-xl font-semibold text-white mb-6">
              {TEMPLATE_TYPES.find(t => t.type === selectedType)?.name} Templates
            </h2>

            {/* Custom Button */}
            <div className="mb-8">
              <button
                onClick={handleCustomMode}
                className="w-full p-6 rounded-2xl border-2 border-dashed border-purple-500/50 hover:border-purple-500 bg-purple-500/10 hover:bg-purple-500/20 transition-all text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/30 flex items-center justify-center">
                    <Icons.Plus className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Create Custom</h3>
                    <p className="text-sm text-slate-400">Build your own with any symbols you want</p>
                  </div>
                </div>
              </button>
            </div>

            {/* Premade Templates */}
            {PREMADE_TEMPLATES[selectedType] && (
              <div>
                <h3 className="text-lg font-medium text-slate-300 mb-4">
                  Or choose a ready-made template
                  
                  {/* My Templates Section */}
                  {user && userTemplates.length > 0 && (
                    <div className="mt-8">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Icons.FolderOpen className="w-5 h-5" />
                        My Templates ({userTemplates.length})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {userTemplates
                          .filter(t => t.type === selectedType)
                          .map((template) => (
                            <div
                              key={template.id}
                              className="bg-slate-700 rounded-xl p-4 border border-slate-600 hover:border-purple-500 transition-colors"
                            >
                              <h4 className="font-semibold text-white mb-1">{template.title}</h4>
                              <p className="text-xs text-slate-400 mb-3">
                                {template.type?.replace(/_/g, ' ')}
                              </p>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setSelectedPremade({ ...template, isUserTemplate: true });
                                    setCustomMode(false);
                                  }}
                                  className="flex-1 px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                  <Icons.Download className="w-4 h-4" />
                                  Generate
                                </button>
                                <button
                                  onClick={async () => {
                                    // Load template for editing
                                    const docRef = doc(db, 'user_templates', template.id);
                                    const docSnap = await getDoc(docRef);
                                    if (docSnap.exists()) {
                                      const templateData = docSnap.data();
                                      loadTemplateForEditing({ ...template, ...templateData });
                                    }
                                  }}
                                  className="flex-1 px-3 py-2 bg-slate-600 hover:bg-slate-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                  <Icons.Pencil className="w-4 h-4" />
                                  Edit
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {PREMADE_TEMPLATES[selectedType].map((premade) => (
                    <div
                      key={premade.id}
                      className="p-5 rounded-xl bg-slate-800 border border-slate-700 hover:border-purple-500/50 transition-all"
                    >
                      <h4 className="font-medium text-white mb-1">{premade.name}</h4>
                      <p className="text-sm text-slate-400 mb-4">
                        {premade.steps && `${premade.steps} steps`}
                        {premade.panels && `${premade.panels} panels`}
                        {premade.choices && `${premade.choices} choices`}
                        {premade.cards && `${premade.cards} cards`}
                        {premade.stars && `${premade.stars} stars`}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePremadeSelect(premade)}
                          className="flex-1 px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <Icons.Download className="w-4 h-4" />
                          Generate
                        </button>
                        <button
                          onClick={() => loadTemplateForEditing(premade)}
                          disabled={loadingTemplate}
                          className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <Icons.Pencil className="w-4 h-4" />
                          Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Premade Template Preview & Generate */}
        {selectedPremade && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-slate-800 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-2">{selectedPremade.name}</h2>
              <p className="text-slate-400 mb-6">
                Ready to generate! Optionally add a student name for personalization.
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Student Name (optional)
                </label>
                <input
                  type="text"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="e.g., Alex"
                  className="w-full px-4 py-3 bg-slate-700 text-white placeholder-slate-400 rounded-xl border border-slate-600 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                />
              </div>

              {isPreviewMode ? (
                <button
                  onClick={() => navigate('/signup/solo')}
                  className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all flex items-center justify-center gap-3"
                >
                  <Icons.LockClosed className="w-5 h-5" />
                  Sign Up to Generate PDFs
                </button>
              ) : (
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {generating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <Icons.Download className="w-5 h-5" />
                      Generate & Download PDF
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3b: Custom Template Builder */}
        {customMode && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-slate-800 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                {editingTemplate ? 'Edit' : 'Create Custom'} {TEMPLATE_TYPES.find(t => t.type === selectedType)?.name}
              </h2>

              {/* Show template origin when editing */}
              {editingTemplate && (
                <div className="mb-6 flex items-center gap-2 text-sm text-purple-400 bg-purple-500/10 px-4 py-2 rounded-lg">
                  <Icons.Document className="w-4 h-4" />
                  <span>Based on: <strong>{editingTemplate.name}</strong></span>
                  <span className="text-slate-400 ml-2">— Modify the items below and regenerate</span>
                </div>
              )}

              {/* Title */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="e.g., Morning Routine"
                  className="w-full px-4 py-3 bg-slate-700 text-white placeholder-slate-400 rounded-xl border border-slate-600 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                />
              </div>

              {/* Student Name */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Student Name (optional)
                </label>
                <input
                  type="text"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="e.g., Alex"
                  className="w-full px-4 py-3 bg-slate-700 text-white placeholder-slate-400 rounded-xl border border-slate-600 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                />
              </div>

              {/* Worksheet-specific options */}
              {selectedType === 'worksheet' && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Worksheet Type
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { id: 'reflection', name: 'Reflection', icon: 'BookOpen' },
                        { id: 'goal_setting', name: 'Goal Setting', icon: 'Target' },
                        { id: 'emotion_checkin', name: 'Emotion Check-In', icon: 'Heart' },
                      ].map(wt => {
                        const WtIcon = Icons[wt.icon] || Icons.Document;
                        return (
                          <button
                            key={wt.id}
                            onClick={() => setWorksheetType(wt.id)}
                            className={`p-3 rounded-xl border-2 transition-all ${worksheetType === wt.id
                              ? 'border-purple-500 bg-purple-500/20'
                              : 'border-slate-600 hover:border-slate-500'
                              }`}
                          >
                            <WtIcon className={`w-5 h-5 mx-auto mb-1 ${worksheetType === wt.id ? 'text-purple-400' : 'text-slate-400'}`} />
                            <span className={`text-xs ${worksheetType === wt.id ? 'text-white' : 'text-slate-400'}`}>{wt.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={showSymbols}
                          onChange={(e) => setShowSymbols(e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-14 h-8 rounded-full transition-colors ${showSymbols ? 'bg-purple-500' : 'bg-slate-600'}`}>
                          <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${showSymbols ? 'translate-x-6' : ''}`}></div>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-300">Show Symbols</span>
                        <p className="text-xs text-slate-500">Include ARASAAC symbols for visual support</p>
                      </div>
                    </label>
                  </div>
                </>
              )}

              {/* Items */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {selectedType === 'now_next_board' ? 'Now & Next Items' :
                    selectedType === 'choice_board' ? 'Choices' :
                      selectedType === 'visual_schedule' ? 'Steps' :
                        selectedType === 'emotion_cards' ? 'Emotions' :
                          selectedType === 'task_analysis' ? 'Task Steps' :
                            selectedType === 'social_story_comic' ? 'Story Panels' :
                              selectedType === 'worksheet' && worksheetType === 'reflection' ? 'Prompts/Questions' :
                                selectedType === 'worksheet' && worksheetType === 'emotion_checkin' ? 'Emotions' :
                                  selectedType === 'worksheet' ? 'Items' :
                                    'Items'} {selectedType === 'worksheet' ? '(optional symbol word)' : '(type a word to find the symbol)'}
                </label>

                <div className="space-y-3">
                  {customItems.map((item, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.word}
                          onChange={(e) => updateCustomItem(index, 'word', e.target.value)}
                          placeholder={
                            selectedType === 'now_next_board'
                              ? (index === 0 ? 'Now symbol (e.g., work)' : 'Next symbol (e.g., play)')
                              : 'Symbol word (e.g., breakfast)'
                          }
                          className="w-full px-4 py-3 bg-slate-700 text-white placeholder-slate-400 rounded-xl border border-slate-600 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.label}
                          onChange={(e) => updateCustomItem(index, 'label', e.target.value)}
                          placeholder="Label (optional)"
                          className="w-full px-4 py-3 bg-slate-700 text-white placeholder-slate-400 rounded-xl border border-slate-600 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                        />
                      </div>
                      {customItems.length > 1 && selectedType !== 'now_next_board' && (
                        <button
                          onClick={() => removeCustomItem(index)}
                          className="p-3 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
                        >
                          <Icons.XMark className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {selectedType !== 'now_next_board' && (
                  <button
                    onClick={addCustomItem}
                    className="mt-3 flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    <Icons.Plus className="w-4 h-4" />
                    Add another item
                  </button>
                )}
              </div>

              {isPreviewMode ? (
                <button
                  onClick={() => navigate('/signup/solo')}
                  className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all flex items-center justify-center gap-3"
                >
                  <Icons.LockClosed className="w-5 h-5" />
                  Sign Up to Generate PDFs
                </button>
              ) : (
                <button
                  onClick={handleGenerate}
                  disabled={generating || customItems.every(i => !i.word.trim())}
                  className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {generating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <Icons.Download className="w-5 h-5" />
                      Generate & Download PDF
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* All resources – non-editable KB activities (same as homepage), so there is always more to see when scrolling */}
        {!selectedType && (
          <section className="mt-12">
            <h2 className="text-xl font-semibold text-white mb-6">All Resources</h2>
            <p className="text-slate-400 mb-6">Browse teaching resources and activities. Tap any card to view details.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {kbActivities.map(activity => {
                const primaryDomain = activity.targetDomains?.[0];
                const domainStyle = getKBDomainStyle(primaryDomain);
                const TileIcon = domainStyle.Icon;
                return (
                  <Link
                    key={activity.id}
                    to={`/solo/activity/${activity.id}`}
                    className="group bg-slate-800 rounded-2xl overflow-hidden hover:ring-2 hover:ring-purple-500/50 transition-all"
                  >
                    <div className={`h-32 bg-gradient-to-br ${domainStyle.gradient} p-4 flex items-end relative overflow-hidden`}>
                      <div className="absolute top-3 right-3 w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                        <TileIcon className={`w-5 h-5 ${domainStyle.iconColor}`} />
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {activity.targetDomains?.slice(0, 2).map(domain => {
                          const domainInfo = KB_DOMAINS.find(d => d.id === domain);
                          const DomainIcon = domainInfo?.Icon || Icons.Puzzle;
                          return (
                            <span
                              key={domain}
                              className="px-2 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs text-white flex items-center gap-1"
                            >
                              <DomainIcon className="w-3 h-3" />
                              {domain}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-400 transition-colors">
                        {activity.title}
                      </h3>
                      <p className="text-slate-400 text-sm line-clamp-2 mb-4">
                        {activity.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {activity.ageRange && (
                          <span>Ages {activity.ageRange.min}-{activity.ageRange.max}</span>
                        )}
                        {activity.recommendedFrequency && (
                          <span>{activity.recommendedFrequency.sessionMinutes} min</span>
                        )}
                        {activity.resources && (
                          <span>{Array.isArray(activity.resources) ? activity.resources.length : '0'} resources</span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Tips Section */}
        {!selectedType && (
          <section className="mt-12">
            <h2 className="text-xl font-semibold text-white mb-6">Tips for Creating Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Use Simple Words',
                  description: 'ARASAAC has 10,000+ symbols. Single words like "eat", "play", "happy" work best.',
                  icon: 'CheckCircle',
                },
                {
                  title: 'Personalize When Possible',
                  description: 'Add the student name to make resources more engaging and meaningful.',
                  icon: 'User',
                },
                {
                  title: 'Print at High Quality',
                  description: 'For best results, use a color printer and laminate for durability.',
                  icon: 'Printer',
                },
              ].map((tip, idx) => {
                const TipIcon = Icons[tip.icon] || Icons.CheckCircle;
                return (
                  <div key={idx} className="bg-slate-800/50 rounded-xl p-5">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
                      <TipIcon className="w-5 h-5 text-purple-400" />
                    </div>
                    <h3 className="text-white font-medium mb-1">{tip.title}</h3>
                    <p className="text-slate-400 text-sm">{tip.description}</p>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-slate-400">
              <img
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Ed"
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-medium text-white">SEN-Ed Resources</span>
            </div>
            <p className="text-sm text-slate-500">
              Pictograms from{' '}
              <a
                href="https://arasaac.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300"
              >
                ARASAAC
              </a>
              {' '}(Aragonese Centre for AAC) - CC BY-NC-SA License
            </p>
          </div>
        </div>
      </footer>

      {/* Save Template Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Save Template</h3>
            <p className="text-slate-400 mb-4">
              Save this template so you can reuse it later.
            </p>
            <input
              type="text"
              value={templateNameToSave}
              onChange={(e) => setTemplateNameToSave(e.target.value)}
              placeholder="Enter template name"
              className="w-full px-4 py-3 bg-slate-700 text-white placeholder-slate-400 rounded-xl border border-slate-600 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none mb-4"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  saveCustomTemplate(templateNameToSave);
                }
              }}
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSaveDialog(false);
                  setTemplateNameToSave('');
                }}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => saveCustomTemplate(templateNameToSave)}
                className="flex-1 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-xl transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceBuilder;

